import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { QRCodeSVG } from 'qrcode.react';

const COLORS = ['#0f172a', '#f97316', '#64748b', '#cbd5e1', '#e2e8f0'];

const AnalyticsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [qrCode, setQrCode] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchData = useCallback(async () => {
    try {
      const [qrResponse, analyticsResponse] = await Promise.all([
        api.get(`/qr/${id}`),
        api.get(`/qr/${id}/analytics`),
      ]);
      setQrCode(qrResponse.data);
      setAnalytics(analyticsResponse.data);
    } catch (error) {
      toast.error(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const downloadQR = () => {
    const svg = document.querySelector('[data-testid="qr-download-preview"] svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `${qrCode.name}-qr-code.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white flex items-center justify-center">
        <p className="text-muted-foreground" data-testid="analytics-loading">{t('loading')}</p>
      </div>
    );
  }

  const scansByDateData = Object.entries(analytics.scansByDate || {}).map(([date, count]) => ({
    date,
    scans: count,
  })).sort((a, b) => a.date.localeCompare(b.date));

  const scansByCountryData = Object.entries(analytics.scansByCountry || {}).map(([name, value]) => ({
    name: name || 'Unknown',
    value,
  }));

  const scansByDeviceData = Object.entries(analytics.scansByDevice || {}).map(([name, value]) => ({
    name,
    value,
  }));

  const scansByBrowserData = Object.entries(analytics.scansByBrowser || {}).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" data-testid="back-to-dashboard-btn">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('back')}
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Logo className="h-8 w-8" />
              <span className="text-2xl font-heading font-bold text-primary">cyQRus</span>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-2" data-testid="analytics-title">
              {t('analytics')}
            </h1>
            <p className="text-lg text-muted-foreground" data-testid="qr-name">{qrCode.name}</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/qr/${qrCode.id}/export/csv`, '_blank')}
              variant="outline" 
              size="sm"
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Button 
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/qr/${qrCode.id}/export/pdf`, '_blank')}
              variant="outline"
              size="sm"
              data-testid="export-pdf-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              PDF
            </Button>
            <Button onClick={downloadQR} variant="outline" data-testid="download-qr-btn">
              <Download className="h-4 w-4 mr-2" />
              {t('download')}
            </Button>
            <Link to={`/edit/${qrCode.id}`}>
              <Button className="btn-accent" data-testid="edit-qr-btn">{t('edit')}</Button>
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="card p-6 space-y-2" data-testid="stat-total-scans">
            <p className="text-sm text-muted-foreground">{t('total_scans')}</p>
            <p className="text-3xl font-heading font-bold text-primary">{analytics.totalScans}</p>
          </div>
          <div className="card p-6 space-y-2 md:col-span-3" data-testid="qr-preview-card">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">QR Code</p>
              <p className="text-xs text-muted-foreground">{backendUrl}/{qrCode.uniqueSlug}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border inline-block" data-testid="qr-download-preview">
              <QRCodeSVG
                value={`${backendUrl}/${qrCode.uniqueSlug}`}
                size={120}
                fgColor={qrCode.customization?.fgColor || '#000000'}
                bgColor={qrCode.customization?.bgColor || '#FFFFFF'}
                level="H"
                includeMargin={true}
              />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="card p-6 space-y-4" data-testid="scans-over-time-chart">
            <h2 className="text-xl font-heading font-semibold text-primary">{t('scans_over_time')}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={scansByDateData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#64748b" style={{ fontSize: '12px' }} />
                <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Line type="monotone" dataKey="scans" stroke="#f97316" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="card p-6 space-y-4" data-testid="scans-by-device-chart">
            <h2 className="text-xl font-heading font-semibold text-primary">{t('scans_by_device')}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={scansByDeviceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {scansByDeviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="card p-6 space-y-4" data-testid="scans-by-country-chart">
            <h2 className="text-xl font-heading font-semibold text-primary">{t('scans_by_country')}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scansByCountryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#64748b" style={{ fontSize: '12px' }} />
                <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Bar dataKey="value" fill="#f97316" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card p-6 space-y-4" data-testid="scans-by-browser-chart">
            <h2 className="text-xl font-heading font-semibold text-primary">{t('scans_by_browser')}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scansByBrowserData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#64748b" style={{ fontSize: '12px' }} />
                <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-6 space-y-4" data-testid="recent-scans-table">
          <h2 className="text-xl font-heading font-semibold text-primary">{t('recent_scans')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Date & Time</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Device</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Browser</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {analytics.scans.map((scan) => (
                  <tr key={scan.id} className="border-b hover:bg-secondary/50">
                    <td className="py-3 px-4">{new Date(scan.timestamp).toLocaleString()}</td>
                    <td className="py-3 px-4">{scan.deviceType || 'Unknown'}</td>
                    <td className="py-3 px-4">{scan.browser || 'Unknown'}</td>
                    <td className="py-3 px-4">{scan.country || 'Unknown'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsPage;
